import { useEffect, useState } from 'react';
import { Globe, Trash2 } from 'lucide-react';
import api from '../../../utils/api';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { Alert, Button, EmptyState, FormField } from '../../ui';

const IP_PATTERN = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}(?:\/(?:[0-9]|[1-2][0-9]|3[0-2]))?$|^\*$/;

const PROPAGATION_NOTE =
  'Allowlist changes take up to 5 minutes to take effect. The allowlist is cached for 300 seconds and neither adding nor removing an entry clears that cache, so a newly added IP is still rejected — and a removed IP is still accepted — until the cache expires. This is expected: wait 5 minutes before re-adding an address that looks like it is not working.';

const WhitelistedIps = ({ ips = [], fetchDetails }) => {
  const [ip, setIp] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState('');

  const uniqueIps = [...new Set(ips)];
  const trimmed = ip.trim();
  const allowlistDisabled = uniqueIps.includes('*');

  useEffect(() => {
    if (!success && !error) return undefined;
    const timer = setTimeout(() => {
      setSuccess('');
      setError('');
    }, 5000);
    return () => clearTimeout(timer);
  }, [success, error]);

  const handleAdd = async () => {
    setError('');
    setSuccess('');

    if (!trimmed) {
      setError('IP address cannot be empty.');
      return;
    }
    if (!IP_PATTERN.test(trimmed)) {
      setError('Invalid IP address or range format.');
      return;
    }
    if (uniqueIps.includes(trimmed)) {
      setError(`${trimmed} is already on the allowlist. Duplicates are not rejected by the server, so it was not sent.`);
      return;
    }

    setBusy(true);
    try {
      await api.post(PartnerEndpoints.whitelistIp, { ip: trimmed });
      setSuccess(`${trimmed} added. It will start being accepted within 5 minutes.`);
      setIp('');
      // The write endpoints answer with data: null, so the new list has to come back from GET /details.
      await fetchDetails?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Something went wrong adding the IP.');
    }
    setBusy(false);
  };

  const removeIp = async (target) => {
    // POST /remove-ip runs no validation: an omitted or empty ip answers 500, not 422.
    if (!target) return;

    setError('');
    setSuccess('');
    setBusy(true);
    try {
      await api.post(PartnerEndpoints.removeIp, { ip: target });
      setSuccess(`${target} removed. It will keep being accepted for up to 5 more minutes.`);
      setConfirmingRemoval('');
      await fetchDetails?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Something went wrong removing the IP.');
    }
    setBusy(false);
  };

  const requestRemoval = (target) => {
    if (uniqueIps.length === 1) {
      setConfirmingRemoval(target);
      return;
    }
    removeIp(target);
  };

  return (
    <div className="space-y-4">
      <Alert variant="info">{PROPAGATION_NOTE}</Alert>

      {allowlistDisabled && (
        <Alert variant="warning">
          <span className="font-semibold">The IP allowlist is switched off.</span> The entry <code>*</code> accepts server
          API requests from every address on the internet. Remove it and add specific addresses to restore the
          restriction.
        </Alert>
      )}

      <FormField
        label="IP address or range"
        htmlFor="whitelist-ip"
        hint="A single address (1.1.1.1) or a CIDR range (2.0.0.0/8)."
      >
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id="whitelist-ip"
            name="ip"
            className="form-control"
            placeholder="e.g. 1.1.1.1 or 2.0.0.0/8"
            value={ip}
            onChange={(event) => setIp(event.target.value)}
          />
          <Button onClick={handleAdd} loading={busy} className="sm:w-32">
            Add
          </Button>
        </div>
      </FormField>

      {trimmed === '*' && (
        <Alert variant="warning">
          <span className="font-semibold">* switches the allowlist off entirely.</span> Adding it lets any address on the
          internet call the server API with your credentials. Add a specific address or range instead unless that is what
          you want.
        </Alert>
      )}

      {success && (
        <Alert variant="success" onDismiss={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      {confirmingRemoval && (
        <Alert variant="danger">
          <p className="font-semibold">Removing {confirmingRemoval} leaves the allowlist empty.</p>
          <p className="mt-1">
            With no entries left, this account is locked out of the server API and every request is rejected. Add a
            replacement address first unless you intend to stop calling the API.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="danger" size="sm" loading={busy} onClick={() => removeIp(confirmingRemoval)}>
              Remove anyway
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setConfirmingRemoval('')}>
              Cancel
            </Button>
          </div>
        </Alert>
      )}

      {uniqueIps.length === 0 ? (
        <EmptyState
          icon={<Globe className="w-8 h-8" />}
          title="No allowlisted IPs"
          message="Server API requests are rejected until at least one address is on the allowlist."
        />
      ) : (
        <ul className="divide-y divide-theme-border dark:divide-themedark-border">
          {uniqueIps.map((item) => (
            <li key={item} className="flex items-center justify-between gap-3 py-2">
              <span className="font-medium text-theme-headings dark:text-themedark-headings">{item}</span>
              <Button
                variant="outline-danger"
                size="sm"
                disabled={busy}
                onClick={() => requestRemoval(item)}
                aria-label={`Remove ${item}`}
              >
                <Trash2 className="w-4 h-4" />
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default WhitelistedIps;
